import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { AlertCircle } from 'lucide-react';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      const loggedUser = result?.user;
      const isShared = loggedUser?.is_shared || loggedUser?.role === 'SHARED';
      toast.success('Login effettuato con successo');
      navigate(isShared ? '/m' : '/app/dashboard');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Errore durante il login';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center relative"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1560339838-a716f22bf4ee?crop=entropy&cs=srgb&fm=jpg&q=85)'
      }}
      data-testid="login-page"
    >
      <div className="absolute inset-0 bg-slate-900/60"></div>
      
      <Card className="w-full max-w-md relative z-10 shadow-2xl" data-testid="login-card">
        <CardHeader className="space-y-1 text-center">
          <h1 className="font-heading text-3xl font-bold text-primary">BollettinoX</h1>
          <p className="text-muted-foreground">Gestionale Bollettini e Fatture</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2 text-red-700">
                <AlertCircle size={18} />
                <span className="text-sm">{error}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email o username</Label>
              <Input
                id="email"
                type="text"
                placeholder="tuo@email.com oppure username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? 'Accesso...' : 'Accedi'}
            </Button>
            <p className="pt-2 text-xs text-slate-500 text-center" data-testid="login-legal-footer">
              Effettuando l&apos;accesso accetti la nostra{' '}
              <a href="/privacy" className="text-blue-700 hover:text-blue-900 underline" data-testid="footer-privacy-link">Privacy Policy</a>
              {' · '}
              <a href="/impressum" className="text-blue-700 hover:text-blue-900 underline" data-testid="footer-impressum-link">Impressum</a>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
